import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import axios from "axios";
import { BACKEND_URL } from "../../utils/constants";

// Admin tool: list / add / remove offline MBTiles tilesets. Uploaded maps appear in
// the Mission Map's base-layer picker (top-right) so the FDC works with no internet.
export default function MapsPanel() {
  const [maps, setMaps] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/tiles`);
      setMaps(Array.isArray(r.data?.tilesets) ? r.data.tilesets : []);
    } catch {
      setMaps([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    setMsg({ sev: "info", text: `Uploading ${f.name}…` });
    try {
      await axios.post(`${BACKEND_URL}/tiles/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg({ sev: "success", text: `Added ${f.name}. Reopen the map to pick it in the layer switcher.` });
      load();
    } catch (err) {
      setMsg({ sev: "error", text: err?.response?.data?.message || "Upload failed." });
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/tiles/${encodeURIComponent(id)}`);
      setMsg({ sev: "success", text: "Map removed." });
      load();
    } catch {
      setMsg({ sev: "error", text: "Failed to remove map." });
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 700 }}>Offline maps</Typography>
        <Chip size="small" label={`${maps.length} loaded`} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" component="label" disabled={busy} startIcon={<UploadFileRoundedIcon />}>
          Add .mbtiles
          <input hidden type="file" accept=".mbtiles" onChange={onFile} />
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Upload raster MBTiles tilesets. Each appears in the Mission Map's base-layer picker (top-right),
        so the FDC has imagery with no internet. Large files may take a moment to upload.
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {maps.map((m) => (
          <Stack
            key={m.id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ fontFamily: "monospace", fontSize: 12.5 }}
          >
            <span style={{ fontWeight: 600 }}>{m.name || m.id}</span>
            <Chip size="small" variant="outlined" label={`z${m.minzoom ?? "?"}–${m.maxzoom ?? "?"}`} />
            {m.size_mb != null && <Chip size="small" variant="outlined" label={`${m.size_mb} MB`} />}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Remove this map">
              <IconButton size="small" color="warning" onClick={() => del(m.id)}>
                <DeleteForeverRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
        {!maps.length && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            No offline maps yet — add a .mbtiles above.
          </Typography>
        )}
      </Stack>
      {msg && (
        <Alert severity={msg.sev} sx={{ mt: 1 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}
    </Box>
  );
}
